import { useState } from 'react';
import { Button, Icon, Tooltip } from '@nazka/nazka.mapframe.components';
import {
  faAdd,
  faCaretDown,
  faCaretUp,
  faLink,
  faMemo,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { faWarning } from '@fortawesome/pro-regular-svg-icons';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import AdminAddCompany from '@App/pages/Admin/AdminAddCompany/AdminAddCompany';
import ConfirmationDialog from '@App/components/ConfirmationDialog/ConfirmationDialog';

import { formatCompanyIdentificationNumber } from '@services/utils.service';
import { deleteCompany } from '@services/company.service';

import theme from '@css/theme.style.json';
import queryClient from '@App/app.query-client';

import { getMobilityOfferIcon } from './admin-side-panel.utils';

import './admin-sidepanel.styl';

function AdminSidepanel({
  activeCompanyId,
  companies,
  copiedCompanyId,
  isLoading,
  setActiveCompanyId,
}) {
  const [collapsedCompanies, setCollapsedCompanies] = useState([]);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [deleteCompanyId, setDeleteCompanyId] = useState(undefined);

  const deleteCompanyMutation = useMutation({
    mutationFn: () => deleteCompany(deleteCompanyId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user', 'companies'] });
      toast.success('De vestiging is verwijderd');
    },
    onError: () => {
      toast.error('Er ging helaas iets mis bij het verwijderen van de vestiging');
    },
    onSettled: () => {
      setDeleteCompanyId(undefined);
    },
  });

  return (
    <div className={`admin-sidepanel${isLoading ? ' is-loading' : ''}`}>
      <h3 className="admin-sidepanel__title">
        Mijn ondernemingen
      </h3>
      <div className="admin-sidepanel__list">
        {companies?.map(({
          NAZKA_NM,
          OND_MZ,
          branches,
          address,
        }) => {
          const companyIsCollapsed = collapsedCompanies.includes(OND_MZ);

          return (
            <div className="admin-sidepanel__list__company" key={OND_MZ}>
              <div className="admin-sidepanel__list__company__header">
                <h3 className="admin-sidepanel__list__company__header__name">
                  {NAZKA_NM || formatCompanyIdentificationNumber(OND_MZ)}
                  <Button
                    layout="ghost"
                    icon={companyIsCollapsed ? faCaretDown : faCaretUp}
                    themeColor="secondary"
                    hover={false}
                    className="admin-sidepanel__list__company__header__name__toggle"
                    onClick={() => {
                      if (companyIsCollapsed) {
                        setCollapsedCompanies(collapsedCompanies.filter(id => id !== OND_MZ));
                      } else {
                        setCollapsedCompanies([...collapsedCompanies, OND_MZ]);
                      }
                    }}
                  />
                </h3>
                {NAZKA_NM && (
                  <p className="admin-sidepanel__list__company__header__company-number">
                    {formatCompanyIdentificationNumber(OND_MZ)}
                  </p>
                )}
                {address && (
                  <p className="admin-sidepanel__list__company__header__address">
                    {address}
                  </p>
                )}
              </div>
              {!companyIsCollapsed && (
                <>
                  <h4 className="admin-sidepanel__list__company__title">
                    Officiële vestigingen of gekende plaatsen van tewerkstelling voor deze onderneming:
                  </h4>
                  <div className="admin-sidepanel__list__company__body">
                    {branches.map((branch) => {
                      const canDelete = branch.OND_VESTNR.startsWith('NZKA') && branch.vacancyCount <= 0;
                      const deleteTooltipId = `admin-sidepanel__list__company__body__item__header__actions__delete__tooltip__${branch.OIDN}`;

                      return (
                        <div
                          className={
                            'admin-sidepanel__list__company__body__item'
                            + `${activeCompanyId === branch.OIDN ? ' admin-sidepanel__list__company__body__item--active' : ''}`
                            + `${copiedCompanyId === branch.OND_VESTNR ? ' admin-sidepanel__list__company__body__item--copied' : ''}`
                          }
                          key={branch.OIDN}
                          onClick={() => setActiveCompanyId(branch.OIDN)}
                        >
                          <div className="admin-sidepanel__list__company__body__item__header">
                            <h4>{branch.NAZKA_NM}</h4>
                            <div className="admin-sidepanel__list__company__body__item__header__actions">
                              <Tooltip
                                id={deleteTooltipId}
                                tooltip={canDelete
                                  ? 'Dit is geen officiële vestiging maar een bij ons gekende tewerkstellingsplaats. Klopt dit niet (meer)? Dan kan je deze hier verwijderen.'
                                  : 'Officiële vestigingen of tewerkstellingsplaatsen met gekoppelde vacatures kunnen niet verwijderd worden.'}
                              >
                                <Button
                                  icon={faTrash}
                                  className="admin-sidepanel__list__company__body__item__header__actions__button"
                                  onClick={() => setDeleteCompanyId(branch.OIDN)}
                                  disabled={!canDelete}
                                  rootElProps={{
                                    'data-tooltip-id': deleteTooltipId,
                                  }}
                                  themeColor="secondary"
                                  iconProps={{
                                    color: canDelete ? undefined : theme.color.common.grey.xlight,
                                  }}
                                />
                              </Tooltip>
                              <Button
                                icon={faLink}
                                className="admin-sidepanel__list__company__body__item__header__actions__button"
                                themeColor="secondary"
                                href={`/${branch.OIDN}`}
                                rootElProps={{ target: '_blank', rel: 'noopener noreferrer' }}
                              />
                            </div>
                          </div>
                          <div className="admin-sidepanel__list__company__body__item__text">
                            {branch.OND_VESTNR.startsWith('NZKA') ? (
                              <span className="admin-sidepanel__list__company__body__item__text__warning">
                                Tewerkstellingsplaats
                                <Tooltip
                                  id={`admin-sidepanel__list__company__body__item__text__warning__tooltip-${branch.OIDN}`}
                                  clickable
                                  tooltip={(
                                    <span className="admin-sidepanel__list__company__body__item__text__warning__tooltip">
                                      Dit is geen officiële vestiging maar een bij ons gekende tewerkstellingsplaats.
                                      Klopt dit niet?
                                      Dan kan je deze verwijderen met de&nbsp;
                                      <Icon
                                        icon={faTrash}
                                        size="14px"
                                        className="admin-sidepanel__list__company__body__item__text__warning__tooltip__icon"
                                      />
                                      -knop.
                                    </span>
                                  )}
                                >
                                  <Icon
                                    className="admin-sidepanel__list__company__body__item__text__warning__icon"
                                    icon={faWarning}
                                    size="16px"
                                    rootElProps={{
                                      'data-tooltip-id': `admin-sidepanel__list__company__body__item__text__warning__tooltip-${branch.OIDN}`,
                                    }}
                                  />
                                </Tooltip>
                              </span>
                            ) : `Vestiging: ${branch.OND_VESTNR}`}
                          </div>
                          <p className="admin-sidepanel__list__company__body__item__text">
                            {branch.address}
                          </p>
                          <div className="admin-sidepanel__list__company__body__item__footer">
                            <Icon
                              className="admin-sidepanel__list__company__body__item__footer__mobiliteitsaanbod"
                              icon={`/img/icons/${getMobilityOfferIcon(branch.mobilityOffer)}`}
                            />
                            <div className="admin-sidepanel__list__company__body__item__footer__vacancies">
                              <Icon
                                className="admin-sidepanel__list__company__body__item__footer__vacancies__icon"
                                icon={faMemo}
                              />
                              <span>
                                {`Momenteel ${branch.vacancyCount} vacatures`}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          )
        })}
      </div>
      <Button
        className="admin-sidepanel__button"
        layout="outlined"
        icon={faAdd}
        themeColor="secondary"
        onClick={() => setShowAddCompanyModal(true)}
      >
        Voeg een ondernemingsnummer toe
      </Button>
      {showAddCompanyModal && (
        <AdminAddCompany
          onClose={() => setShowAddCompanyModal(false)}
        />
      )}
      {deleteCompanyId && (
        <ConfirmationDialog
          title="Verwijder tewerkstellingsplaats"
          content={(
            <>
              Zeker dat u deze tewerkstellingsplaats wilt verwijderen?
              Dit kan niet ongedaan gemaakt worden.
            </>
          )}
          onConfirm={deleteCompanyMutation.mutate}
          onClose={() => {
            setDeleteCompanyId(undefined);
          }}
          confirmButtonLoading={deleteCompanyMutation.isPending}
        />
      )}
    </div>
  )
}

export default AdminSidepanel;